import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Footer from '../components/Footer';

const PrivacyPolicyPage = () => {
  const lastUpdated = 'April 10, 2025';

  return (
    <>
      <Box
        sx={{
          bgcolor: 'background.paper',
          py: { xs: 8, md: 10 },
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h1"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '2.25rem', md: '3rem' },
            }}
          >
            Privacy Policy
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ mb: 1 }}
          >
            Last Updated: {lastUpdated}
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            align="center"
            sx={{ maxWidth: '800px', mx: 'auto', mt: 3 }}
          >
            At QResto, we take your privacy seriously. This Privacy Policy explains how we collect, use, and protect your personal information.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            mb: 4,
            borderRadius: 2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Introduction
          </Typography>
          <Typography variant="body1" paragraph>
            This Privacy Policy applies to information we collect when you use our websites, mobile applications, and other online products and services (collectively, the "Services") or when you otherwise interact with us, including when you sign up for an account, attend our events, or communicate with our customer service.
          </Typography>
          <Typography variant="body1" paragraph>
            We may change this Privacy Policy from time to time. If we make changes, we will notify you by revising the date at the top of the policy and, in some cases, we may provide you with additional notice (such as adding a statement to our homepage or sending you a notification).
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Information We Collect
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Information You Provide to Us
          </Typography>
          <Typography variant="body1" paragraph>
            We collect information you provide directly to us. For example, we collect information when you create an account, participate in any interactive features of the Services, fill out a form, make a purchase, apply for a job, communicate with us via third-party social media sites, request customer support, or otherwise communicate with us. The types of information we may collect include your name, email address, postal address, phone number, restaurant information, payment information, and any other information you choose to provide.
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
            Information We Collect Automatically When You Use the Services
          </Typography>
          <Typography variant="body1" paragraph>
            When you access or use our Services, we automatically collect information about you, including:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Log Information"
                secondary="We log information about your use of our Services, including the type of browser you use, access times, pages viewed, your IP address, and the page you visited before navigating to our Services."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Device Information"
                secondary="We collect information about the computer or mobile device you use to access our Services, including the hardware model, operating system and version, unique device identifiers, and mobile network information."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Location Information"
                secondary="We may collect information about the location of your device each time you access or use one of our mobile applications or otherwise consent to the collection of this information."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Information Collected by Cookies and Other Tracking Technologies"
                secondary="We use various technologies to collect information, including cookies, web beacons, and similar technologies. Cookies are small data files stored on your hard drive or in device memory that help us improve our Services and your experience."
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            How We Use Information
          </Typography>
          <Typography variant="body1" paragraph>
            We may use information about you for various purposes, including to:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Provide, maintain, and improve our Services"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Process transactions and send you related information, including confirmations and invoices"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Send you technical notices, updates, security alerts, and support and administrative messages"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Respond to your comments, questions, and requests and provide customer service"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Communicate with you about products, services, offers, promotions, rewards, and events offered by QResto and others, and provide news and information we think will be of interest to you"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Monitor and analyze trends, usage, and activities in connection with our Services"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Detect, investigate, and prevent fraudulent transactions and other illegal activities and protect the rights and property of QResto and others"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Personalize and improve the Services and provide content or features that match user profiles or interests"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Facilitate contests, sweepstakes, and promotions and process and deliver entries and rewards"
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Sharing of Information
          </Typography>
          <Typography variant="body1" paragraph>
            We may share information about you as follows or as otherwise described in this Privacy Policy:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="With vendors, consultants, and other service providers who need access to such information to carry out work on our behalf"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="In response to a request for information if we believe disclosure is in accordance with any applicable law, regulation, or legal process"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="If we believe your actions are inconsistent with the spirit or language of our user agreements or policies, or to protect the rights, property, and safety of QResto or others"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="In connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Between and among QResto and our current and future parents, affiliates, subsidiaries, and other companies under common control and ownership"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="With your consent or at your direction, including if we notify you through our Services that the information you provide will be shared in a particular manner and you provide such information"
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Data Security
          </Typography>
          <Typography variant="body1" paragraph>
            QResto takes reasonable measures to help protect information about you from loss, theft, misuse, and unauthorized access, disclosure, alteration, and destruction.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Your Choices
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Account Information
          </Typography>
          <Typography variant="body1" paragraph>
            You may update, correct, or delete information about you at any time by logging into your online account or emailing us at privacy@qresto.com. If you wish to delete or deactivate your account, please email us, but note that we may retain certain information as required by law or for legitimate business purposes.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
            Cookies
          </Typography>
          <Typography variant="body1" paragraph>
            Most web browsers are set to accept cookies by default. If you prefer, you can usually choose to set your browser to remove or reject browser cookies. Please note that if you choose to remove or reject cookies, this could affect the availability and functionality of our Services.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
            Promotional Communications
          </Typography>
          <Typography variant="body1" paragraph>
            You may opt out of receiving promotional communications from QResto by following the instructions in those communications or by sending an email to unsubscribe@qresto.com. If you opt out, we may still send you non-promotional communications, such as those about your account or our ongoing business relations.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have any questions about this Privacy Policy, please contact us at:
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            Email: privacy@qresto.com
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            Address: 123 Restaurant Ave, Food City, FC 12345, India
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            Phone: +91 98765 43210
          </Typography>
        </Paper>
      </Container>
      
      <Footer />
    </>
  );
};

export default PrivacyPolicyPage;